import axios from 'axios';
import { rememberTenantPublicKey } from '../utils/tenantPublicKey';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Public API client — no auth. Per-call x-api-key is supplied by the tenant.
const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Short-lived "phone-verified" token issued by POST /sms (validate). Required
// by the public web flow on PUT /invitation/{id}, PUT /business_access/{id},
// and POST /business_access (event registration) — without it the lambdas
// reject the public api-key path. See sms_token.py in the core layer.
const SMS_TOKEN_KEY = 'public_sms_token';

const setSmsToken = (token: string | null) => {
  if (token) sessionStorage.setItem(SMS_TOKEN_KEY, token);
  else sessionStorage.removeItem(SMS_TOKEN_KEY);
};

const getSmsToken = (): string | null => {
  try {
    return sessionStorage.getItem(SMS_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearSmsToken = () => setSmsToken(null);

export interface PublicEvent {
  id: number;
  name: string;
  name_es?: string;
  name_en?: string;
  short_description_es?: string;
  short_description_en?: string;
  description?: string;
  address?: string;
  start_date?: string;
  end_date?: string;
  capacity?: number;
  active?: boolean;
  logo_url?: string;
  background_img_url?: string;
  banner_mobile_url?: string;
  phone_number?: string;
  business_id?: string;
  tenant_id?: string;
  event_type_id?: number;
  event_type?: { id: number; name: string };
  /** SAM-730: private events need the event code to register (details stay public). */
  is_private?: boolean;
  /** SAM-730: only on GET /event/event/{id} sent with X-Event-Code — whether that code is right. */
  code_valid?: boolean;
  access_settings?: {
    id: number;
    name: string;
    settings: {
      event: CompanionTemplate[];
    };
  };
}

export interface EventCustomCategory {
  key: string;
  min: number;
  max: number;
  title_en: string;
  title_es: string;
  subtitle_en?: string;
  subtitle_es?: string;
  qty?: number;
  // When true the first guest of this category becomes the access main_guest.
  // See CustomCategory in useAccessSettings.ts for the full semantics.
  is_main_guest?: boolean;
}

export interface CompanionTemplate {
  short_name: string;
  title_en: string;
  title_es: string;
  adults?: {
    min: number;
    max: number;
    title_en: string;
    title_es: string;
    subtitle_en: string;
    subtitle_es: string;
    qty?: number;
  };
  child?: {
    min_num: number;
    max_num: number;
    name_en: string;
    name_es: string;
    subtitle_en: string;
    subtitle_es: string;
    qty?: number;
  };
  // Extra categories defined per access_settings (e.g. athlete, vip, spectator).
  // Mirrors CustomCategory in useAccessSettings — exposed here so public consumers
  // can iterate without importing the admin hook.
  categories?: EventCustomCategory[];
}

export interface TenantInfo {
  id: string;
  name: string;
  domain?: string;
  settings?: Record<string, any>;
  /** tenant-branding `features` (SAM-1065): whatsapp_code is true only while the tenant can send WhatsApp codes. */
  features?: { whatsapp_code?: boolean };
}

// Fetch public events for a tenant
export const getPublicEvents = async (tenantId?: string, apiKey?: string): Promise<PublicEvent[]> => {
  const params: Record<string, string> = {
    order_by: 'start_date',
    order_direction: 'desc',
    expand: 'event_group,event_group_id,event_type,event_type_id,access_settings,access_settings_id',
  };
  if (tenantId) params.tenant_id = tenantId;

  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.get('/event/event', { params, headers });
  return response.data?.data ?? response.data ?? [];
};

// Fetch a single event
export const getPublicEvent = async (eventId: string, apiKey?: string): Promise<PublicEvent | null> => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.get(`/event/event/${eventId}`, {
    params: { expand: 'event_type,event_type_id,access_settings,access_settings_id' },
    headers,
  });
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data[0] : data;
};

// SAM-730: check a private event's code. GET /event/event/{id} with
// X-Event-Code answers `code_valid` without ever returning the code itself.
// Anything but an explicit `true` (wrong code, older backend) counts as invalid.
export const checkEventCode = async (eventId: string | number, code: string, apiKey?: string): Promise<boolean> => {
  const headers: Record<string, string> = { 'X-Event-Code': code };
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.get(`/event/event/${eventId}`, { headers });
  const data = response.data?.data ?? response.data;
  const event = Array.isArray(data) ? data[0] : data;
  return event?.code_valid === true;
};

/** SAM-730: POST /business_access rejected the event code (403 EVENT_CODE_INVALID). */
export const isEventCodeRejected = (err: unknown): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = (err as any)?.response;
  return res?.status === 403 && res?.data?.code === 'EVENT_CODE_INVALID';
};

/**
 * SAM-737: the backend limits failed event-code attempts. Both the code check and the
 * registration POST answer 429 EVENT_CODE_LOCKED (Retry-After header / `retry_after`
 * body, in seconds) once the limit is reached, and 503 EVENT_CODE_UNAVAILABLE when the
 * attempt store is down. Returns null for any other error.
 */
export type EventCodeDenial = { kind: 'locked'; minutes: number } | { kind: 'unavailable' };

export const eventCodeDenial = (err: unknown): EventCodeDenial | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = (err as any)?.response;
  const code = res?.data?.code;
  if (res?.status === 503 && code === 'EVENT_CODE_UNAVAILABLE') return { kind: 'unavailable' };
  if (res?.status !== 429 || code !== 'EVENT_CODE_LOCKED') return null;
  const header = res?.headers?.['retry-after'] ?? res?.headers?.['Retry-After'];
  const seconds = Number(header ?? res?.data?.retry_after);
  const minutes = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : 1;
  return { kind: 'locked', minutes };
};

// Fetch tenant info
export const getTenantInfo = async (tenantId: string): Promise<TenantInfo | null> => {
  const response = await publicClient.get(`/crud/tenant/${tenantId}`);
  const data = response.data?.data ?? response.data;
  return Array.isArray(data) ? data[0] : data;
};

// sessionStorage key for the cached branding. Bumped to v2 when the backend
// stopped serving the tenant's first-party api_key: entries written under the
// old key hold that private key and are purged below.
export const TENANT_BRANDING_CACHE_PREFIX = 'tenant_branding_v3_';
const LEGACY_TENANT_BRANDING_CACHE_PREFIX = 'tenant_branding_';

// Fetch tenant branding by domain (public, no auth needed, cached in sessionStorage).
// `settings.apiKey` is the tenant's *public web* key (tenant.settings.public_api_key):
// low-privilege, only accepted by the guest flow endpoints (events, sms, invitation
// confirmation, guest registration). It is not the admin/business api key.
// Every useTenantBranding() on a page asks for the same tenant while the first answer is still on its way;
// they share one request instead of racing the sessionStorage cache (each one used to hit the lambda).
const tenantInFlight = new Map<string, Promise<TenantInfo | null>>();
export const getTenantByDomain = (domain: string): Promise<TenantInfo | null> => {
  const pending = tenantInFlight.get(domain);
  if (pending) return pending;
  const request = fetchTenantByDomain(domain)
    .then((tenant) => { rememberTenantPublicKey(tenant?.settings?.apiKey); return tenant; })
    .finally(() => tenantInFlight.delete(domain));
  tenantInFlight.set(domain, request);
  return request;
};

const fetchTenantByDomain = async (domain: string): Promise<TenantInfo | null> => {
  const cacheKey = `${TENANT_BRANDING_CACHE_PREFIX}${domain}`;
  try {
    sessionStorage.removeItem(`${LEGACY_TENANT_BRANDING_CACHE_PREFIX}${domain}`);
    sessionStorage.removeItem(`tenant_branding_v2_${domain}`); // before `features`
  } catch {}
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    try { return JSON.parse(cached); } catch {}
  }

  try {
    const response = await publicClient.get('/tenant-branding', {
      params: { domain },
    });
    const data = response.data;
    if (!data || data.error) return null;
    const tenant: TenantInfo = {
      id: data.id || '',
      name: data.name || '',
      domain,
      features: { whatsapp_code: data.features?.whatsapp_code === true },
      settings: {
        logo: data.logo || '',
        primaryColor: data.primaryColor || '',
        favicon: data.favicon || '',
        apiKey: data.publicApiKey || data.apiKey || '',
      },
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(tenant));
    return tenant;
  } catch {
    return null;
  }
};

// Fetch country list for phone code selector
export interface Country {
  country: string;
  country_code: string;
  phone_code: string;
  country_flag: string;
}

export const getCountries = async (apiKey?: string): Promise<Country[]> => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.get('/country', { headers });
  return response.data?.data ?? response.data ?? [];
};

// Generate Apple Wallet pass (same payload as Flutter WalletEventModel.toJson)
export const generateWalletPass = async (data: {
  qrLink: string;
  code: string;
  type: string;
  companyName: string;
  from: string;
  to: string;
  destination?: string;
  gate?: string;
  reservedBy?: string;
  persons?: number;
  businessName?: string;
  location?: string;
}, apiKey?: string): Promise<string | null> => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.post('/wallet', {
    ...data,
    destination: data.destination || '',
    gate: data.gate || '',
  }, { headers });
  return response.data?.url || null;
};

// Analyze document image via OCR (same as Flutter POST /ocrservice/image?action=analyze)
export interface OcrResult {
  name: string;
  LastName: string;
  number: string;
  DOB: string;
  nationality: string;
  expDate: string;
  image?: string;
  picture?: string;
}

export const analyzeDocument = async (base64Image: string, fileName: string, apiKey?: string): Promise<OcrResult> => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.post('/ocrservice/image', {
    image: base64Image,
    filename: fileName,
    mimetype: 'image/jpg',
  }, {
    params: { action: 'analyze', savetype: 'base64' },
    headers,
  });
  return response.data?.data ?? response.data;
};

// Channel the verification code is delivered through. The sendSMSCode lambda
// reads `delivery_method` (SMS | WHATSAPP); WHATSAPP async-invokes the
// sendWhatsAppCode lambda (Twilio WhatsApp template). EMAIL is not implemented
// backend-side. See functions/sendSMSCode/lambda_function.py.
export type CodeDeliveryMethod = 'SMS' | 'WHATSAPP';

// Send verification code (same as Flutter POST /sms/v2).
// `captchaToken` is required when the public web api-key is in use — the
// lambda verifies it against Cloudflare's siteverify before issuing the code.
// Flutter calls this same endpoint with a business api-key and skips it.
export const sendSmsCode = async (
  phoneNumber: string,
  apiKey?: string,
  captchaToken?: string,
  deliveryMethod: CodeDeliveryMethod = 'SMS',
) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.post('/sms/v2', {
    phone_number: phoneNumber,
    type: 'guest-code',
    delivery_method: deliveryMethod,
    ...(captchaToken ? { captcha_token: captchaToken } : {}),
  }, { headers });
  return response.data;
};

// Verify SMS PIN code (same as Flutter POST /sms).
// On success the lambda returns an `sms_token` field (short-lived JWT bound
// to the verified phone). We persist it in sessionStorage so subsequent
// PUT /invitation/{id} and PUT /business_access/{id} calls can present it
// as X-Sms-Token — required for public-api-key writes.
export const verifySmsCode = async (phoneNumber: string, code: string, apiKey?: string) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.post('/sms', {
    phone_number: phoneNumber,
    code,
    type: 'validate',
  }, { headers });

  const data = response.data || {};
  if (data.sms_token) {
    setSmsToken(data.sms_token);
  }
  return data;
};

// Validate phone for private events (same as Flutter GET /event/event_phone_numbers)
export const validateEventPhone = async (phoneNumber: string, eventId: string, apiKey?: string) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;

  const response = await publicClient.get('/event/event_phone_numbers', {
    params: { phone_number: phoneNumber, event_id: eventId },
    headers,
  });
  return response.data;
};

// Create event access / register for event (same as Flutter POST /business_access)
export const createEventAccess = async (data: {
  business_id?: string;
  event_id: string | number;
  /** SAM-730: required by the backend for private events. */
  event_code?: string;
  start_date?: string;
  end_date?: string;
  children_count?: number;
  main_guest: {
    name: string;
    last_name: string;
    tel: string;
    title_es?: string;
    // OCR-derived fields. When present, the access is created already-confirmed
    // (is_analyzed=true → status_id=2 in the lambda).
    idnum?: string;
    photo_url?: string;
    nationality?: string;
    birthdate?: string;
    // Slug of the category the main guest belongs to (e.g. 'athlete'). Stored
    // in the main_guest JSONB so analytics can group properly even when no
    // category is flagged is_main_guest.
    category_key?: string;
  };
  business_guests?: Array<{
    name: string;
    last_name: string;
    title_es?: string;
    idnum?: string;
    photo_url?: string;
    nationality?: string;
    birthdate?: string;
    // Slug of the CustomCategory this guest belongs to (e.g. 'athlete', 'vip').
    // Lets the backend group / report companions by category.
    category_key?: string;
  }>;
}, apiKey?: string) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;
  const smsToken = getSmsToken();
  if (smsToken) headers['X-Sms-Token'] = smsToken;

  // The lambda flips business_access_status_id to 2 when is_analyzed=true,
  // and to 1 otherwise. We pass is_analyzed=true since the user already
  // completed phone verification + ID scan in the events flow.
  const response = await publicClient.post('/business_access', {
    ...data,
    // SAM-199: `services_id: 3` removed — dead field, see the same fix in
    // DocumentScanForm.tsx / AddInvitation.tsx. The DB column is `service_id`
    // (uuid FK) and businessAccess/lambda_function.py's `_normalize_service_id`
    // only keeps a value that parses as a UUID, so the literal 3 was always
    // dropped server-side on this /business_access POST too.
    id_channel: 1,
    created_by: 105,
    modified_by: 105,
    business_access_type_id: 2,
    is_analyzed: true,
  }, { headers });
  return response.data;
};

export interface InvitationUserUpdate {
  name?: string;
  last_name?: string;
  photo_url?: string;
  idnum?: string;
  nationality?: string;
  birthdate?: string;
  tel?: string;
}

export interface InvitationCompanionUpdate extends InvitationUserUpdate {
  user_id?: number;
  id?: number;
}

// Confirm an invitation and (optionally) update main_guest / companions in one call.
// The invitation lambda's PUT handler uses its IAM role to write user records, so the
// public api-key alone is enough — no Cognito JWT required.
// When status_id=2 the backend auto-generates the Hik-Connect PIN.
export const acceptInvitation = async (
  invitationId: number,
  payload?: { status_id?: number; main_guest?: InvitationUserUpdate; companions?: InvitationCompanionUpdate[] },
  apiKey?: string,
) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;
  const smsToken = getSmsToken();
  if (smsToken) headers['X-Sms-Token'] = smsToken;

  const body = { status_id: 2, ...(payload || {}) };
  const response = await publicClient.put(`/invitation/${invitationId}`, body, { headers });
  return response.data;
};

// Confirm a business_access (UUID id) and update main_guest / companions in one call.
// Mirrors acceptInvitation but hits the business_access lambda. Default status_id=2 (confirmed).
export const acceptBusinessAccess = async (
  businessAccessId: string,
  payload?: { business_access_status_id?: number; main_guest?: InvitationUserUpdate; companions?: InvitationCompanionUpdate[] },
  apiKey?: string,
) => {
  const headers: Record<string, string> = {};
  if (apiKey) headers['X-Api-Key'] = apiKey;
  const smsToken = getSmsToken();
  if (smsToken) headers['X-Sms-Token'] = smsToken;

  const body = { business_access_status_id: 2, ...(payload || {}) };
  const response = await publicClient.put(`/business_access/${businessAccessId}`, body, { headers });
  return response.data;
};
